<template>
  <div class="security-tools-page">
    <header class="page-head">
      <h1>Security Tools</h1>
      <p class="sub">Quick IP intelligence for moderation &amp; triage.</p>
    </header>

    <!-- Inline toast -->
    <p v-if="errorMessage" class="toast error">
      {{ errorMessage }}
      <button
        class="toast__dismiss"
        @click="errorMessage = ''"
        aria-label="Dismiss"
      >
        ×
      </button>
    </p>

    <!-- Query card -->
    <section class="card -tight">
      <form class="ip-form" @submit.prevent="lookupIp" novalidate>
        <div class="input-wrap" :class="{ invalid: showInvalid }">
          <label for="ipInput">IP Address</label>
          <div class="row">
            <input
              id="ipInput"
              v-model.trim="ipAddress"
              type="text"
              inputmode="numeric"
              spellcheck="false"
              placeholder="e.g., 8.8.8.8"
              @keyup.enter="lookupIp"
              @input="clearFeedback"
              :aria-invalid="showInvalid ? 'true' : 'false'"
            />
            <button
              class="btn btn--primary"
              type="submit"
              :disabled="isLoading"
            >
              <span v-if="!isLoading">Lookup</span>
              <span v-else class="spinner"></span>
            </button>
            <button
              class="btn btn--ghost"
              type="button"
              @click="clearAll"
              :disabled="isLoading"
            >
              Clear
            </button>
          </div>
          <small v-if="showInvalid" class="field-hint">
            Enter a valid IPv4 (or IPv6) address.
          </small>
        </div>
      </form>
    </section>

        <!-- Result card -->
    <section v-if="ipInfo" class="card">
      <header class="card__head">
        <div>
          <h2>IP Information</h2>
          <p v-if="sourceLabel" class="card__meta">{{ sourceLabel }}</p>
        </div>
        <div class="chips">
          <span
            class="pill"
            :class="riskIsWarn ? 'pill--warn' : 'pill--ok'"
          >
            {{ riskLabel }}
          </span>
        </div>
      </header>
      <div class="kv-grid">
        <!-- Overall VPN/hosting verdict -->
        <div class="kv">
          <dt>VPN / Hosting Verdict</dt>
          <dd :class="isVpnOrHosting ? 'text-warn' : 'text-ok'">
            {{ vpnHostingVerdict }}
          </dd>
        </div>

        <!-- NEW: Region / country eligibility -->
        <div class="kv">
          <dt>Region Eligibility</dt>
          <dd :class="isEligibleCountry ? 'text-ok' : 'text-warn'">
            {{ countryEligibilityLabel }}
          </dd>
        </div>

        <div class="kv">
          <dt>IP Address</dt>
          <dd class="mono flex">
            <span>{{ ipInfo.ip || '—' }}</span>
            <button
              class="icon-btn"
              title="Copy IP"
              @click="copy(ipInfo.ip)"
            >
              📋
            </button>
          </dd>
        </div>

        <div class="kv">
          <dt>Hostname</dt>
          <dd class="mono wrap">{{ ipInfo.hostname || '—' }}</dd>
        </div>

        <div class="kv">
          <dt>City</dt>
          <dd>{{ ipInfo.city || '—' }}</dd>
        </div>

        <div class="kv">
          <dt>Region</dt>
          <dd>{{ ipInfo.region || '—' }}</dd>
        </div>

        <div class="kv">
          <dt>Country</dt>
          <dd>{{ ipInfo.country || '—' }}</dd>
        </div>

        <div class="kv">
          <dt>Postal Code</dt>
          <dd>{{ ipInfo.postal || '—' }}</dd>
        </div>

        <div class="kv">
          <dt>Timezone</dt>
          <dd class="mono">{{ ipInfo.timezone || '—' }}</dd>
        </div>

        <div class="kv">
          <dt>Latitude</dt>
          <dd class="mono">{{ ipInfo.latitude ?? '—' }}</dd>
        </div>

        <div class="kv">
          <dt>Longitude</dt>
          <dd class="mono">{{ ipInfo.longitude ?? '—' }}</dd>
        </div>

        <div class="kv">
          <dt>ISP / Org</dt>
          <dd class="wrap">{{ ipInfo.isp || '—' }}</dd>
        </div>
<div class="kv">
  <dt>ASN</dt>
  <dd class="mono">{{ ipInfo.asn || '—' }}</dd>
</div>

<div class="kv">
  <dt>ASN Name / Route</dt>
  <dd class="wrap">
    <span v-if="ipInfo.asn_name">
      {{ ipInfo.asn_name }}
      <span v-if="ipInfo.asn_route"> — {{ ipInfo.asn_route }}</span>
    </span>
    <span v-else-if="ipInfo.asn_route">{{ ipInfo.asn_route }}</span>
    <span v-else>—</span>
  </dd>
</div>

        <div class="kv">
          <dt>Map</dt>
          <dd>
            <a
              v-if="hasCoordinates"
              class="link"
              :href="mapsLink"
              target="_blank"
              rel="noopener"
            >
              Open in Maps →
            </a>
            <span v-else>—</span>
          </dd>
        </div>

        <!-- New: Company -->
        <div class="kv">
          <dt>Company</dt>
          <dd>
            <span v-if="ipInfo.company_name">
              {{ ipInfo.company_name }}
              <span v-if="ipInfo.company_type">
                ({{ ipInfo.company_type }})
              </span>
            </span>
            <span v-else>—</span>
          </dd>
        </div>

        <div class="kv">
          <dt>Company Domain</dt>
          <dd class="mono">
            {{ ipInfo.company_domain || '—' }}
          </dd>
        </div>

        <!-- New: Network flags -->
        <div class="kv">
          <dt>Network Flags</dt>
          <dd>
            <span v-if="networkFlags">
              {{ networkFlags }}
            </span>
            <span v-else>None detected</span>
          </dd>
        </div>

        <!-- New: Privacy details -->
        <div class="kv">
          <dt>Privacy / Anonymous</dt>
          <dd>
            <span v-if="privacySummary">
              {{ privacySummary }}
            </span>
            <span v-else>None detected</span>
          </dd>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import axios from 'axios';

export default {
  name: 'SecurityTools',
  data() {
    return {
      ipAddress: '',
      ipInfo: null,
      rawIpInfo: null,
      ipSource: '',
      errorMessage: '',
      isLoading: false
    };
  },
  computed: {
    networkFlags() {
      if (!this.ipInfo) return '';
      const i = this.ipInfo;
      const flags = [];

      if (i.is_hosting) flags.push('Hosting/datacenter');
      if (i.is_anycast) flags.push('Anycast');
      if (i.is_mobile) flags.push('Mobile');
      if (i.is_satellite) flags.push('Satellite');
      if (i.is_anonymous && !i.is_vpn && !i.privacy_proxy && !i.privacy_tor) {
        flags.push('Anonymous');
      }

      return flags.join(', ');
    },
  // NEW: is this country within supported region (US/Canada)?
    isEligibleCountry() {
      if (!this.ipInfo || !this.ipInfo.country) return false;
      const c = String(this.ipInfo.country).trim().toUpperCase();

      // Handle both 2-letter and full names to be safe
      return (
        c === 'US' ||
        c === 'USA' ||
        c === 'UNITED STATES' ||
        c === 'CA' ||
        c === 'CANADA'
      );
    },

    // NEW: human-readable label for region eligibility
    countryEligibilityLabel() {
      if (!this.ipInfo) return '';
      const country = this.ipInfo.country || 'Unknown';

      if (!this.ipInfo.country) {
        return 'Unknown location (no country returned)';
      }

      if (this.isEligibleCountry) {
        return `Within supported region (US/Canada: ${country})`;
      }

      return `Outside supported region (${country})`;
    },
    // NEW: overall boolean verdict (true = behind VPN/hosting/proxy/Tor/edge)
    isVpnOrHosting() {
      if (!this.ipInfo) return false;
      const i = this.ipInfo;
      return !!(
        i.is_vpn ||
        i.privacy_proxy ||
        i.privacy_tor ||
        i.privacy_relay ||
        i.privacy_hosting ||
        i.is_hosting ||
        i.is_anycast ||
        i.is_satellite
      );
    },

    // NEW: human-readable verdict label
    vpnHostingVerdict() {
      if (!this.ipInfo) return '';
      const i = this.ipInfo;

      if (!this.isVpnOrHosting) {
        return 'No clear VPN/hosting signal (likely residential / normal IP)';
      }

      const bits = [];
      if (i.is_vpn) bits.push('VPN');
      if (i.privacy_proxy) bits.push('Proxy');
      if (i.privacy_tor) bits.push('Tor');
      if (i.privacy_relay) bits.push('Relay');
      if (i.is_hosting || i.privacy_hosting) bits.push('Hosting/datacenter');
      if (i.is_anycast) bits.push('Anycast edge');
      if (i.is_satellite) bits.push('Satellite');

      const uniqueBits = [...new Set(bits)];
      const detail = uniqueBits.length ? ` (${uniqueBits.join(', ')})` : '';

      return 'Yes — traffic is likely coming from VPN/hosting or similar infrastructure' + detail;
    },

    showInvalid() {
      if (!this.ipAddress) return false;
      return !this.isValidIp(this.ipAddress);
    },
    hasCoordinates() {
      const lat = this.ipInfo?.latitude;
      const lon = this.ipInfo?.longitude;
      return (
        lat !== undefined &&
        lon !== undefined &&
        lat !== null &&
        lon !== null &&
        `${lat}` !== '' &&
        `${lon}` !== ''
      );
    },
    mapsLink() {
      if (!this.hasCoordinates) return '#';
      const { latitude: lat, longitude: lon } = this.ipInfo;
      return `https://www.google.com/maps?q=${encodeURIComponent(
        lat
      )},${encodeURIComponent(lon)}`;
    },

    // Human-readable label for where the data came from
    sourceLabel() {
      if (!this.ipSource) return '';
      if (this.ipSource === 'cache') return 'From cache (≤ 30 days)';
      if (this.ipSource === 'live') return 'Live lookup (IPinfo)';
      return this.ipSource;
    },

    // Risk pill content
    riskIsWarn() {
      if (!this.ipInfo) return false;
      const i = this.ipInfo;
      return !!(
        i.is_vpn ||
        i.is_anonymous ||
        i.privacy_proxy ||
        i.privacy_tor ||
        i.is_hosting
      );
    },
    riskLabel() {
      if (!this.ipInfo) return '';
      const i = this.ipInfo;

      const bits = [];
      if (i.is_vpn) bits.push('VPN');
      if (i.privacy_proxy) bits.push('Proxy');
      if (i.privacy_tor) bits.push('Tor');
      if (i.is_hosting) bits.push('Hosting');
      if (i.is_anonymous && bits.length === 0) bits.push('Anonymous');

      if (bits.length === 0) return 'Residential / normal IP';
      return bits.join(', ') + ' detected';
    },

    // Privacy details summary
    privacySummary() {
      if (!this.ipInfo) return '';
      const i = this.ipInfo;
      const bits = [];

      if (i.is_vpn) bits.push('VPN');
      if (i.privacy_proxy) bits.push('Proxy');
      if (i.privacy_tor) bits.push('Tor');
      if (i.privacy_relay) bits.push('Relay');
      if (i.privacy_hosting) bits.push('Hosting');
      if (i.privacy_service) bits.push(`Service: ${i.privacy_service}`);

      return bits.join(', ');
    }
  },
  methods: {
    clearFeedback() {
      this.errorMessage = '';
    },
    clearAll() {
      this.ipAddress = '';
      this.ipInfo = null;
      this.rawIpInfo = null;
      this.ipSource = '';
      this.errorMessage = '';
    },
    copy(text) {
      if (!text) return;
      navigator.clipboard?.writeText(text).catch(() => {});
    },
    isValidIp(ip) {
      // IPv4
      const v4 =
        /^(25[0-5]|2[0-4]\d|1?\d?\d)\.(25[0-5]|2[0-4]\d|1?\d?\d)\.(25[0-5]|2[0-4]\d|1?\d?\d)\.(25[0-5]|2[0-4]\d|1?\d?\d)$/;
      // Simplified IPv6
      const v6 =
        /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|(([0-9a-fA-F]{1,4}:){1,7}:)|(([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4})|(([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2})|(([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3})|(([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4})|(([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5})|([0-9a-fA-F]{1,4}:)((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:))$/;
      return v4.test(ip) || v6.test(ip);
    },
    lookupIp() {
      if (!this.isValidIp(this.ipAddress)) {
        this.errorMessage = 'Please enter a valid IP address.';
        this.ipInfo = null;
        this.rawIpInfo = null;
        this.ipSource = '';
        return;
      }
      this.errorMessage = '';
      this.isLoading = true;
      this.ipInfo = null;
      this.rawIpInfo = null;
      this.ipSource = '';

      axios
        .post(
          'etherscan/other/check_ip.php',
          { ip_address: this.ipAddress },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        )
        .then((response) => {
          const payload = response.data || {};
          if (payload.status === 'success') {
            this.ipInfo = payload.data || null;
            this.rawIpInfo = payload.raw || null;
            this.ipSource = payload.source || '';

            if (this.rawIpInfo) {
              // Inspect this in the browser devtools console when you want
              console.log('IPinfo raw payload:', this.rawIpInfo);
            }

            if (!this.ipInfo) {
              this.errorMessage = 'No data returned for this IP.';
            }
          } else {
            this.errorMessage =
              payload.message || 'Lookup failed.';
          }
        })
        .catch((error) => {
          const res = error?.response;
          const serverMessage = res?.data?.message;
          const debugMessage = res?.data?.debug;

          if (serverMessage) {
            this.errorMessage = 'Error retrieving IP information: ' + serverMessage;
          } else {
            this.errorMessage =
              'Error retrieving IP information. ' + (error?.message || '');
          }

          // Log full server payload in the console for deeper debugging
          if (res?.data) {
            console.error('IP lookup server error payload:', res.data);
          } else {
            console.error('IP lookup error (no server payload):', error);
          }

          if (debugMessage) {
            console.warn('IP lookup debug:', debugMessage);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>

<style scoped>
/* Page shell */
.security-tools-page { max-width:1120px; margin:0 auto; padding:1rem; color:#1f2a37; }
.page-head h1 { margin:0; font-size:1.5rem; font-weight:800; color:#0f2740; }
.sub { color:#667085; margin:.15rem 0 .8rem; }

/* Toasts */
.toast {
  position: relative; padding:.6rem .8rem; border-radius:10px; margin:.2rem 0 1rem;
  font-weight:600; border:1px solid transparent;
}
.toast.error   { background:#fef2f2; color:#991b1b; border-color:#fecaca; }
.toast__dismiss { position:absolute; top:6px; right:8px; border:none; background:transparent; font-size:1.1rem; cursor:pointer; }

/* Cards */
.card {
  background:#fff; border:1px solid #e5e7eb; border-radius:14px;
  box-shadow:0 10px 20px rgba(16,24,40,.06); padding:1rem; margin-bottom:1rem;
}
.card.-tight { padding:.9rem; }
.card__head { display:flex; justify-content:space-between; align-items:center; margin-bottom:.6rem; }
.card__meta { margin:.15rem 0 0; font-size:.78rem; color:#9ca3af; }

/* Chips / Pills */
.chips { display:flex; gap:.4rem; flex-wrap:wrap; }
.pill {
  display:inline-block; padding:.15rem .55rem; border-radius:999px; font-size:.78rem; font-weight:700; border:1px solid transparent;
}
.pill--ok   { background:#e8faf3; color:#065f46; border-color:#a7f3d0; }
.pill--warn { background:#fff7ed; color:#9a3412; border-color:#fed7aa; }

/* Form */
.ip-form .input-wrap { display:flex; flex-direction:column; gap:.35rem; }
.ip-form label { font-weight:700; color:#0f2740; }
.ip-form .row { display:flex; gap:.45rem; flex-wrap:wrap; }
.ip-form input {
  flex:1 1 320px;
  border:1px solid #e5e7eb; border-radius:10px; padding:.6rem .7rem;
  font-size:1rem; background:#fff;
}
.input-wrap.invalid input { border-color:#fecaca; background:#fff7f7; }
.field-hint { color:#b91c1c; }

/* Buttons */
.btn {
  padding:.55rem .8rem; border-radius:10px; border:1px solid rgba(15,38,61,.15);
  background:linear-gradient(180deg,#ffffff,#f3f4f6); cursor:pointer; font-weight:600;
}
.btn:hover { background:linear-gradient(180deg,#f9fafb,#eef2f7); }
.btn:disabled { opacity:.6; cursor:not-allowed; }
.btn--ghost { background:#f9fafb; border:1px solid #e5e7eb; }
.btn--primary { background:#0ea5e9; border-color:#0284c7; color:#fff; }
.btn--primary:hover { background:#0284c7; }

/* Spinner */
.spinner {
  display:inline-block; width:16px; height:16px; border:2px solid #fff; border-right-color:transparent; border-radius:50%;
  animation: spin .7s linear infinite; vertical-align:-2px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Result grid */
.kv-grid {
  display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap:.75rem;
}
@media (max-width: 760px) { .kv-grid { grid-template-columns: 1fr; } }

.kv { display:grid; grid-template-columns: 160px 1fr; gap:.35rem; align-items:baseline; }
.kv dt { color:#6b7280; }
.kv dd { margin:0; }
.wrap { word-break: break-word; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }
.flex { display:flex; align-items:center; gap:.35rem; }
.icon-btn {
  border:1px solid #e5e7eb; background:#fff; border-radius:8px; padding:.1rem .35rem; cursor:pointer;
}
.icon-btn:hover { background:#f3f4f6; }

/* Links */
.link { color:#0f2740; text-decoration: underline; }
.link:hover { text-decoration: none; }

.text-ok {
  color: #065f46;
  font-weight: 600;
}

.text-warn {
  color: #9a3412;
  font-weight: 600;
}

</style>
